import {
  FormEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { BrowserRouter as Router, Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import {
  Activity,
  AlertTriangle,
  BadgeCheck,
  Check,
  ChevronDown,
  ChevronRight,
  FileText,
  FlaskConical,
  HeartPulse,
  Home,
  IdCard,
  Info,
  Languages,
  LogOut,
  Pill,
  Plus,
  Search,
  ShieldCheck,
  Stethoscope,
  User,
  UserCheck,
  X,
} from 'lucide-react'

import { PatientBanner } from './components/PatientBanner'
import { ExplainMyChartLauncher } from './components/ExplainMyChart'
import { ProviderExperience } from './components/ProviderExperience'
import { PortableProof } from './components/PortableProof'
import { ImmunizationsPanel } from './components/ImmunizationsPanel'
import { DevicesPanel } from './components/DevicesPanel'
import { Documents, RecentEncounters } from './components/ClinicalPanels'
import { VitalsPanel } from './components/VitalsPanel'
import { PatientRegistration } from './components/PatientRegistration'
import { NhiRegistrationWizard } from './components/NhiRegistrationWizard'
import { PatientSearch } from './components/PatientSearch'
import { PatientProfile } from './components/PatientProfile'
import { LoginPage } from './components/LoginPage'
import { tf, th, tn, tl, dTitle, dSub, statusLabel, statusFromLabel, t, fmt, useLang, toggleLang, vocab, demoText, portalActionLabel } from './lib/i18n'
import { DOSE_UNITS, FORMS, FREQUENCIES, ROUTES } from './data/sig-lookups'
import { termsApi, type TermResult, type RegistrationResponse } from './lib/api'

/* ============================================================
   Types
   ============================================================ */

type LookupItem = Record<string, string>
type RecordItem = Record<string, string | number>

type Field = {
  key: string
  label: string
  placeholder?: string
  type?: 'text' | 'date' | 'select' | 'textarea'
  options?: string[]
  lookupSystem?: string // resolve via terminology service (fhir-route, fhir-doseform, fhir-timing, ucum)
  fullWidth?: boolean // render spanning both grid columns
}

type DomainConfig = {
  key: string
  labelKey: string
  eyebrow: string
  route: string
  icon: ReactNode
  accent: string
  bg: string
  ring: string
  recordColumns: { key: string; label: string }[]
  lookupColumns: { key: string; label: string }[]
  formFields: Field[]
  lookup: LookupItem[]
  /**
   * When set, the lookup panel fetches rows from the terminology service
   * (GET /api/v1/terms/search?system=X) instead of the hardcoded `lookup`
   * array. A string is a single system; an array queries each system and
   * merges the results (used by the procedures domain across OPCS-4/CCAM/
   * SBCS/ICD-10-PCS).
   */
  lookupSystem?: string | string[]
  initialRecords: RecordItem[]
  /**
   * Optional dual-provenance block. When present, DomainPage renders a mode
   * toggle (patient-reported / prescribed) and swaps the visible fieldsets.
   * `patientFields` / `clinicianFields` are keys into the shared field
   * registry (see `FIELD_REGISTRY` below).
   */
  provenance?: false | {
    toggleLabel: string // e.g. 'Reported by'
    patientLabel: string // e.g. 'Patient reported'
    clinicianLabel: string // e.g. 'Prescribed'
    patientFields: string[] // field keys shown in patient mode
    clinicianFields: string[] // field keys shown in clinician mode
  }
}

/**
 * Shared field registry — provenance modes reference fields by key so the
 * same Field definition (label, type, lookupSystem) is reused across modes.
 */
const FIELD_REGISTRY: Record<string, Field> = {
  code: { key: 'code', get label() { return tf('code') }, get placeholder() { return th('searchCode') } },
  name: { key: 'name', get label() { return tf('name') }, get placeholder() { return th('enterName') } },
  status: {
    key: 'status',
    get label() { return tf('status') },
    type: 'select',
    get options() { return [t('status.active'), t('status.paused'), t('status.completed'), t('status.stopped')] },
  },
  // Meds — patient mode (narrative only)
  medDescription: {
    key: 'description',
    get label() { return tf('medication') },
    get placeholder() { return t('ph.medNarrative') },
  },
  medNote: {
    key: 'note',
    get label() { return tf('howTake') },
    get placeholder() { return t('ph.medHowTake') },
    type: 'textarea',
    fullWidth: true,
  },
  // Meds — clinician mode (discrete SIG)
  medName: {
    key: 'name',
    get label() { return tf('medication') },
    get placeholder() { return t('ph.medName') },
  },
  medDose: { key: 'dose', get label() { return tf('dose') }, placeholder: '500' },
  medDoseUnit: { key: 'dose_unit', get label() { return tf('doseUnit') }, type: 'select', lookupSystem: 'ucum' },
  medRoute: { key: 'route', get label() { return tf('route') }, type: 'select', lookupSystem: 'fhir-route' },
  medForm: { key: 'form', get label() { return tf('form') }, type: 'select', lookupSystem: 'fhir-doseform' },
  medFrequency: { key: 'frequency_code', get label() { return tf('frequency') }, type: 'select', lookupSystem: 'fhir-timing' },
  medDuration: { key: 'duration', get label() { return tf('duration') }, get placeholder() { return t('ph.duration') } },
  medQuantity: { key: 'quantity', get label() { return tf('quantity') }, get placeholder() { return t('ph.quantity') } },
  medRefills: { key: 'refills', get label() { return tf('refills') }, placeholder: '0' },
  medIndication: { key: 'indication', get label() { return tf('indication') }, get placeholder() { return t('ph.indication') } },
  medPrescriber: { key: 'prescriber', get label() { return tf('prescriber') }, get placeholder() { return t('ph.prescriber') } },
  medSigNote: {
    key: 'note',
    get label() { return tf('sigNote') },
    get placeholder() { return t('ph.sigNote') },
    type: 'textarea',
    fullWidth: true,
  },
}

/** Resolve a list of field keys to Field definitions (skips unknown keys). */
function fieldsByKeys(keys: string[]): Field[] {
  return keys.map((key) => FIELD_REGISTRY[key]).filter((field): field is Field => Boolean(field))
}

/**
 * Fetch terminology options for a `lookupSystem` from the terminology service
 * (`GET /api/v1/terms/search?system=X`). Falls back to the bundled local
 * sig-lookups data when the API is unreachable (offline / tests), so the form
 * still renders usable options.
 */
function useTerminologyOptions(system: string): { code: string; name: string }[] {
  const [options, setOptions] = useState<{ code: string; name: string }[]>(() => sigOptions(system))

  useEffect(() => {
    let cancelled = false
    // Seed with the local fallback immediately, then refresh from the API.
    setOptions(sigOptions(system))
    termsApi
      .search(system, '')
      .then((res) => {
        if (cancelled) return
        const mapped = res.results.map((term: TermResult) => ({ code: term.code, name: term.name }))
        if (mapped.length > 0) setOptions(mapped)
      })
      .catch(() => {
        /* keep local fallback on API failure */
      })
    return () => {
      cancelled = true
    }
  }, [system])

  return options
}

/**
 * Fetch lookup-table rows from the terminology service when a domain sets
 * `lookupSystem` (single system or array). Falls back to the hardcoded
 * `config.lookup` array when no lookupSystem is set or the API is unreachable.
 */
function useTerminologyLookup(
  lookupSystem: string | string[] | undefined,
  fallback: LookupItem[],
  query: string,
): LookupItem[] {
  const [rows, setRows] = useState<LookupItem[]>(fallback)

  useEffect(() => {
    if (!lookupSystem) {
      setRows(fallback)
      return
    }
    let cancelled = false
    const systems = Array.isArray(lookupSystem) ? lookupSystem : [lookupSystem]
    const q = query.trim()
    Promise.all(
      systems.map((system) =>
        termsApi
          .search(system, q || 'a', 50)
          .then((res) => res.results.map((term: TermResult) => ({ ...term.extra, code: term.code, name: term.name, system: term.system })))
          .catch(() => [] as LookupItem[]),
      ),
    ).then((groups) => {
      if (cancelled) return
      const merged = groups.flat()
      if (merged.length > 0) setRows(merged)
    })
    return () => {
      cancelled = true
    }
  }, [lookupSystem, query, fallback])

  return rows
}

/* ============================================================
   Domain configuration (config-driven architecture preserved)
   ============================================================ */

const domains: DomainConfig[] = [
  {
    key: 'labs',
    labelKey: 'labs',
    eyebrow: 'LOINC',
    route: '/labs',
    icon: <FlaskConical className="h-5 w-5" />,
    accent: 'text-emerald-700',
    bg: 'bg-emerald-50',
    ring: 'ring-emerald-200',
    recordColumns: [
      { key: 'code', get label() { return tf('code') } },
      { key: 'name', get label() { return tf('observation') } },
      { key: 'value', get label() { return tf('value') } },
      { key: 'unit', get label() { return tf('unit') } },
      { key: 'date', get label() { return tf('date') } },
      { key: 'range', get label() { return tf('range') } },
    ],
    lookupColumns: [
      { key: 'code', label: 'LOINC' },
      { key: 'name', get label() { return tf('test') } },
      { key: 'component', get label() { return tf('component') } },
      { key: 'system', get label() { return tf('system') } },
      { key: 'scale', get label() { return tf('scale') } },
    ],
    formFields: [
      { key: 'code', label: 'LOINC', get placeholder() { return th('searchCode') } },
      { key: 'name', get label() { return tf('test') }, get placeholder() { return t('ph.labsName') } },
      { key: 'value', get label() { return tf('value') }, placeholder: '7.4' },
      { key: 'unit', get label() { return tf('unit') }, placeholder: '%, mg/dL, mmol/L' },
      { key: 'date', get label() { return tf('date') }, type: 'date' },
      { key: 'range', get label() { return tf('range') }, placeholder: '4.0-5.6' },
    ],
    provenance: false,
    lookup: [
      { code: '4548-4', name: 'Hemoglobin A1c', component: 'Hemoglobin A1c', system: 'Blood', scale: 'Qn' },
      { code: '2339-0', name: 'Glucose', component: 'Glucose', system: 'Blood', scale: 'Qn' },
      { code: '718-7', name: 'Hemoglobin', component: 'Hemoglobin', system: 'Blood', scale: 'Qn' },
      { code: '2093-3', name: 'Total Cholesterol', component: 'Cholesterol', system: 'Serum', scale: 'Qn' },
      { code: '8480-6', name: 'Systolic Blood Pressure', component: 'BP systolic', system: 'Arterial', scale: 'Qn' },
      { code: '8462-4', name: 'Diastolic Blood Pressure', component: 'BP diastolic', system: 'Arterial', scale: 'Qn' },
      { code: '22748-8', name: 'Ferritin', component: 'Ferritin', system: 'Serum', scale: 'Qn' },
      { code: '62238-1', name: 'Vitamin D 25-OH', component: 'Calcidiol', system: 'Serum', scale: 'Qn' },
      { code: '3016-3', name: 'TSH', component: 'Thyrotropin', system: 'Serum', scale: 'Qn' },
    ],
    initialRecords: [
      { id: 1, code: '4548-4', name: 'Hemoglobin A1c', value: '7.4', unit: '%', date: '2026-08-12', range: '4.0-5.6' },
      { id: 2, code: '2339-0', name: 'Glucose', value: '126', unit: 'mg/dL', date: '2026-08-12', range: '70-100' },
      { id: 3, code: '2093-3', name: 'Total Cholesterol', value: '185', unit: 'mg/dL', date: '2026-08-10', range: '<200' },
    ],
  },
  {
    key: 'meds',
    labelKey: 'meds',
    eyebrow: 'RxNorm IN / MIN',
    route: '/meds',
    icon: <Pill className="h-5 w-5" />,
    accent: 'text-indigo-700',
    bg: 'bg-indigo-50',
    ring: 'ring-indigo-200',
    recordColumns: [
      { key: 'name', get label() { return tf('medication') } },
      { key: 'reported_by', get label() { return tf('provenance') } },
      { key: 'note', get label() { return tf('note') } },
      { key: 'status', get label() { return tf('status') } },
    ],
    lookupColumns: [
      { key: 'code', label: 'RxCUI' },
      { key: 'name', get label() { return tf('name') } },
      { key: 'ingredient', label: 'IN' },
      { key: 'multiIngredient', label: 'MIN' },
      { key: 'form', get label() { return tf('form') } },
    ],
    formFields: [
      { key: 'code', label: 'RxCUI', get placeholder() { return t('ph.searchRxNorm') } },
      { key: 'name', get label() { return tf('medication') }, get placeholder() { return t('ph.medName') } },
      { key: 'status', get label() { return tf('status') }, type: 'select', get options() { return [t('status.active'), t('status.paused'), t('status.completed'), t('status.stopped')] } },
    ],
    provenance: {
      get toggleLabel() { return tf('provenanceToggle') },
      get patientLabel() { return tf('patientReported') },
      get clinicianLabel() { return tf('prescribed') },
      // Patient mode: narrative textarea only (no discrete SIG fields).
      patientFields: ['medDescription', 'medNote', 'code', 'status'],
      // Clinician mode: discrete SIG fields with terminology lookups.
      clinicianFields: [
        'code',
        'medName',
        'medDose',
        'medDoseUnit',
        'medRoute',
        'medForm',
        'medFrequency',
        'medDuration',
        'medQuantity',
        'medRefills',
        'medIndication',
        'medPrescriber',
        'medSigNote',
        'status',
      ],
    },
    lookup: [
      { code: '6809', name: 'Metformin', ingredient: 'Metformin', multiIngredient: '-', form: 'IN' },
      { code: '860975', name: 'Metformin 500 MG Oral Tablet', ingredient: 'Metformin', multiIngredient: '-', form: 'Tablet' },
      { code: '617314', name: 'Atorvastatin 20 MG Oral Tablet', ingredient: 'Atorvastatin', multiIngredient: '-', form: 'Tablet' },
      { code: '1998', name: 'Amlodipine', ingredient: 'Amlodipine', multiIngredient: '-', form: 'IN' },
      { code: '197361', name: 'Amlodipine 5 MG / Atorvastatin 20 MG', ingredient: 'Amlodipine + Atorvastatin', multiIngredient: 'MIN', form: 'Tablet' },
      { code: '1191', name: 'Aspirin', ingredient: 'Aspirin', multiIngredient: '-', form: 'IN' },
      { code: '11289', name: 'Warfarin', ingredient: 'Warfarin', multiIngredient: '-', form: 'IN' },
    ],
    initialRecords: [
      {
        id: 1,
        code: '860975',
        name: 'Metformin 500 MG Oral Tablet',
        reported_by: 'clinician',
        dose: '500',
        dose_unit: 'mg',
        route: 'Oral',
        form: 'Tablet',
        frequency_code: 'BID',
        duration: '30 days',
        quantity: '60 tablets',
        refills: 0,
        indication: 'Type 2 diabetes mellitus',
        prescriber: 'Dr. Layla Haddad',
        note: 'Take one tablet by mouth twice daily with meals.',
        status: 'Active',
      },
      {
        id: 2,
        code: '617314',
        name: 'Atorvastatin 20 MG Oral Tablet',
        reported_by: 'clinician',
        dose: '20',
        dose_unit: 'mg',
        route: 'Oral',
        form: 'Tablet',
        frequency_code: 'QHS',
        duration: '90 days',
        quantity: '90 tablets',
        refills: 2,
        indication: 'Hyperlipidemia',
        prescriber: 'Dr. Layla Haddad',
        note: 'Take one tablet by mouth at bedtime.',
        status: 'Active',
      },
      {
        id: 3,
        code: '1998',
        name: 'Amlodipine',
        reported_by: 'patient',
        note: 'I take it orally twice a day.',
        status: 'Active',
      },
    ],
  },
  {
    key: 'diagnoses',
    labelKey: 'diagnoses',
    eyebrow: 'ICD-10-WHO',
    route: '/diagnoses',
    icon: <FileText className="h-5 w-5" />,
    accent: 'text-sky-700',
    bg: 'bg-sky-50',
    ring: 'ring-sky-200',
    recordColumns: [
      { key: 'code', get label() { return tf('code') } },
      { key: 'name', get label() { return tf('condition') } },
      { key: 'since', get label() { return tf('since') } },
      { key: 'status', get label() { return tf('status') } },
    ],
    lookupColumns: [
      { key: 'code', label: 'ICD-10' },
      { key: 'name', get label() { return tf('preferredTerm') } },
      { key: 'category', get label() { return tf('category') } },
    ],
    formFields: [
      { key: 'code', label: 'ICD-10', get placeholder() { return t('ph.searchIcd10') } },
      { key: 'name', get label() { return tf('condition') }, get placeholder() { return t('ph.indication') } },
      { key: 'since', get label() { return tf('since') }, type: 'date' },
      { key: 'status', get label() { return tf('status') }, type: 'select', get options() { return [t('status.active'), t('status.resolved'), t('status.historic')] } },
    ],
    provenance: false,
    // Diagnoses are served by the live terminology service (system='icd10',
    // WHO international edition — 12,318 seeded codes) instead of only the
    // hardcoded fallback list; ADR-021 locks the problem-list view to ICD-10-WHO.
    lookupSystem: 'icd10',
    lookup: [
      { code: 'E11', name: 'Type 2 diabetes mellitus', category: 'Endocrine' },
      { code: 'I10', name: 'Hypertension', category: 'Cardiovascular' },
      { code: 'N18.3', name: 'Chronic kidney disease stage 3', category: 'Nephrology' },
      { code: 'I21', name: 'Acute myocardial infarction', category: 'Cardiovascular' },
      { code: 'J45', name: 'Asthma', category: 'Respiratory' },
      { code: 'D56.1', name: 'Beta thalassaemia', category: 'Hematologic' },
      { code: 'B18.1', name: 'Chronic viral hepatitis B without delta-agent', category: 'Infectious disease' },
      { code: 'D50.9', name: 'Iron deficiency anaemia, unspecified', category: 'Hematologic' },
    ],
    initialRecords: [
      { id: 1, code: 'E11', name: 'Type 2 diabetes mellitus', since: '2018-04-10', status: 'Active' },
      { id: 2, code: 'I10', name: 'Hypertension', since: '2015-11-02', status: 'Active' },
      { id: 3, code: 'N18.3', name: 'Chronic kidney disease stage 3', since: '2025-03-18', status: 'Active' },
    ],
  },
  {
    key: 'allergies',
    labelKey: 'allergies',
    eyebrow: 'SNOMED CT',
    route: '/allergies',
    icon: <AlertTriangle className="h-5 w-5" />,
    accent: 'text-rose-700',
    bg: 'bg-rose-50',
    ring: 'ring-rose-200',
    recordColumns: [
      { key: 'code', get label() { return tf('code') } },
      { key: 'name', get label() { return tf('allergen') } },
      { key: 'reaction', get label() { return tf('reaction') } },
      { key: 'severity', get label() { return tf('severity') } },
      { key: 'status', get label() { return tf('status') } },
    ],
    lookupColumns: [
      { key: 'code', label: 'SNOMED' },
      { key: 'name', get label() { return tf('allergen') } },
      { key: 'category', get label() { return tf('category') } },
    ],
    formFields: [
      { key: 'code', label: 'SNOMED', get placeholder() { return t('ph.searchSnomed') } },
      { key: 'name', get label() { return tf('allergen') }, get placeholder() { return t('ph.allergen') } },
      { key: 'reaction', get label() { return tf('reaction') }, type: 'select', get options() { return [t('rxn.urticaria'), t('rxn.anaphylaxis'), t('rxn.rash'), t('rxn.angioedema')] } },
      { key: 'severity', get label() { return tf('severity') }, type: 'select', get options() { return [t('status.mild'), t('status.moderate'), t('status.severe'), t('status.lifeThreatening')] } },
      { key: 'status', get label() { return tf('status') }, type: 'select', get options() { return [t('status.active'), t('status.inactive'), t('status.enteredInError')] } },
    ],
    provenance: false,
    lookup: [
      { code: '79803003', name: 'Penicillin', category: 'Drug' },
      { code: '91936005', name: 'Peanut', category: 'Food' },
      { code: '26079004', name: 'Latex', category: 'Environmental' },
      { code: '293586001', name: 'Aspirin', category: 'Drug' },
    ],
    initialRecords: [
      { id: 1, code: '79803003', name: 'Penicillin', reaction: 'Urticaria', severity: 'Moderate', status: 'Active' },
    ],
  },
  {
    key: 'procedures',
    labelKey: 'procedures',
    eyebrow: 'OPCS-4 / CCAM / SBCS',
    route: '/procedures',
    icon: <Activity className="h-5 w-5" />,
    accent: 'text-amber-700',
    bg: 'bg-amber-50',
    ring: 'ring-amber-200',
    recordColumns: [
      { key: 'code', get label() { return tf('code') } },
      { key: 'name', get label() { return tf('procedure') } },
      { key: 'system', get label() { return tf('system') } },
      { key: 'date', get label() { return tf('date') } },
      { key: 'status', get label() { return tf('status') } },
    ],
    lookupColumns: [
      { key: 'code', get label() { return tf('code') } },
      { key: 'name', get label() { return tf('procedure') } },
      { key: 'system', get label() { return tf('system') } },
      { key: 'category', get label() { return tf('category') } },
    ],
    formFields: [
      { key: 'code', get label() { return tf('procedure') + ' · ' + th('codeShort') }, get placeholder() { return t('ph.searchProc') } },
      { key: 'name', get label() { return tf('procedure') }, get placeholder() { return t('ph.procedure') } },
      { key: 'system', get label() { return tf('system') }, type: 'select', get options() { return ['OPCS-4', 'CCAM', 'SBCS'] } },
      { key: 'date', get label() { return tf('date') }, type: 'date' },
      { key: 'status', get label() { return tf('status') }, type: 'select', get options() { return [t('status.completed'), t('status.planned'), t('status.cancelled')] } },
    ],
    provenance: false,
    // Procedures are served by the terminology service (OPCS-4/CCAM/SBCS/
    // ICD-10-PCS) rather than a hardcoded list — see TODO.md data gap.
    lookupSystem: ['opcs4', 'ccam', 'sbcs', 'pcs'],
    lookup: [
      { code: 'OPCS4:H22.9', name: 'Appendectomy', system: 'OPCS-4', category: 'Digestive' },
      { code: 'CCAM:ZQXA001', name: 'Appendicectomy', system: 'CCAM', category: 'Digestive' },
      { code: 'OPCS4:K75.8', name: 'Coronary artery bypass graft', system: 'OPCS-4', category: 'Cardiovascular' },
      { code: 'SBCS:00.50', name: 'Appendectomy', system: 'SBCS', category: 'General Surgery' },
    ],
    initialRecords: [
      { id: 1, code: 'OPCS4:H22.9', name: 'Appendectomy', system: 'OPCS-4', date: '1997-06-14', status: 'Completed' },
      { id: 2, code: 'SBCS:00.50', name: 'Appendectomy mapping', system: 'SBCS', date: '1997-06-14', status: 'Completed' },
    ],
  },
]

/* ============================================================
   Verification tiers (from Patient Registration infographic)
   NOTE: The L1–L4 tier grid moved off the main dashboard into the
   PatientBanner "Identity details" disclosure (see AssuranceTierGrid in
   components/PatientBanner.tsx). getVerificationTiers() is kept as the
   dashboard-level helper (dead for now) in case the registrar surfaces
   reuse it; labels still come from the shared VERIFICATION_TIERS source of
   truth (lib/verificationTiers.ts) so they can't drift from PatientBanner.
   ============================================================ */

function getVerificationTiers() {
  return [
    {
      level: 1,
      name: t('tier.name1'),
      desc: t('tier.desc1'),
      icon: <User className="h-4 w-4" />,
      tone: 'text-stone-600 bg-stone-100 border-stone-200',
    },
    {
      level: 2,
      name: t('tier.name2'),
      desc: t('tier.desc2'),
      icon: <IdCard className="h-4 w-4" />,
      tone: 'text-sky-700 bg-sky-50 border-sky-200',
    },
    {
      level: 3,
      name: t('tier.name3'),
      desc: t('tier.desc3'),
      icon: <UserCheck className="h-4 w-4" />,
      tone: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    },
    {
      level: 4,
      name: t('tier.name4'),
      desc: t('tier.desc4'),
      icon: <BadgeCheck className="h-4 w-4" />,
      tone: 'text-primary-700 bg-primary-50 border-primary-200',
    },
  ]
}

/* ============================================================
   App shell
   ============================================================ */

/**
 * Patient-facing home chart patient — the seeded demo record driving the
 * banner, Explain My Chart, encounters, and documents.
 *
 * Explain My Chart's contract tests pin the historical demo id
 * (`11111111-…`), but the seeded live database generates patient UUIDs at
 * insert time (apps/api/app/scripts/seed_demo_patients.py is idempotent by
 * NHI, not UUID), so the live clinical sections resolve via env override:
 * set VITE_CHART_PATIENT_ID to the seeded row's patient_id. The fallback id
 * below is the current demo-database Ahmad Al-Khatib row (NHI
 * SYR-1978-100001 — the richest seeded chart: meds, encounters + notes,
 * documents). If the DB is reseeded with fresh UUIDs, the sections render
 * their honest error state rather than fabricated rows.
 */
const EXPLAIN_DEMO_PATIENT_ID = '11111111-1111-1111-1111-111111111111'
const CHART_PATIENT_ID =
  (import.meta.env.VITE_CHART_PATIENT_ID as string | undefined) ??
  '7f381bff-5fa9-4fee-92a0-f8e21a163e19'

type AppRole = 'patient' | 'provider'

function App() {
  const lang = useLang()
  const isStandaloneNhi = typeof window !== 'undefined' && window.location.pathname.startsWith('/nhi/register')
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (typeof window === 'undefined') return false
    // Test bypass: in vitest (import.meta.env.MODE === 'test') every render is
    // authenticated by default. Read eagerly here — the test-bypass branch of
    // a useState initializer must not hide a localStorage getter, because a
    // stale transpile cache can evaluate `import.meta.env.MODE` AFTER the
    // window/localStorage globals are torn down (jsdom teardown order), which
    // throws a ReferenceError during React's commit phase re-invocation and
    // unmounts the whole app mid-test.
    try {
      if (import.meta.env.MODE === 'test') return true
      return localStorage.getItem('syriaphr_auth') === 'true'
    } catch {
      return false
    }
  })
  const [role, setRole] = useState<AppRole>(() => {
    if (typeof window === 'undefined') return 'patient'
    try {
      const stored = localStorage.getItem('syriaphr_role')
      if (stored === 'provider' || stored === 'patient') return stored
    } catch { /* localStorage unavailable */ }
    return window.location.pathname.startsWith('/provider') ? 'provider' : 'patient'
  })

  function handleLogin(selectedRole: AppRole) {
    setIsAuthenticated(true)
    setRole(selectedRole)
    try {
      localStorage.setItem('syriaphr_auth', 'true')
      localStorage.setItem('syriaphr_role', selectedRole)
    } catch { /* localStorage unavailable */ }
  }

  function handleLogout() {
    setIsAuthenticated(false)
    try {
      localStorage.removeItem('syriaphr_auth')
      localStorage.removeItem('syriaphr_role')
    } catch { /* localStorage unavailable */ }
  }

  return (
    <Router>
      <div
        dir={isStandaloneNhi ? 'ltr' : lang === 'ar' ? 'rtl' : 'ltr'}
        className={
          isStandaloneNhi
            ? 'min-h-screen overflow-x-clip bg-[#f2f7f5] text-stone-950'
            : 'min-h-screen overflow-x-clip bg-[#f7f8f5] text-stone-950'
        }
      >
        {isStandaloneNhi ? (
          <Routes>
            <Route path="/nhi/register" element={<NhiRegisterPage />} />
          </Routes>
        ) : !isAuthenticated ? (
          <Routes>
            <Route path="*" element={<LoginPage onLogin={handleLogin} />} />
          </Routes>
        ) : (
          <>
            <div className="fixed inset-0 -z-10 bg-[linear-gradient(#d9e7df_1px,transparent_1px),linear-gradient(90deg,#d9e7df_1px,transparent_1px)] bg-[size:44px_44px] opacity-40" />
            <Shell
              role={role}
              onToggleRole={(nextRole) => setRole(nextRole)}
              onLogout={handleLogout}
            >
              <AnimatedRoutes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/register" element={<PatientPortal mode="register" />} />
                <Route path="/search" element={<PatientPortal mode="search" />} />
                <Route path="/provider" element={<ProviderExperience />} />
                <Route path="/provider/patients/:patientId" element={<ProviderExperience />} />
                {domains.map((domain) => (
                  <Route key={domain.key} path={domain.route} element={<DomainPage config={domain} />} />
                ))}
              </AnimatedRoutes>
            </Shell>
          </>
        )}
      </div>
    </Router>
  )
}

/* Page transition wrapper — fades/slides on route change */
function NhiRegisterPage() {
  const location = useLocation()
  return <NhiRegistrationWizard locationSearch={location.search} />
}

function AnimatedRoutes({ children }: { children: ReactNode }) {
  const location = useLocation()
  return (
    <div key={location.pathname} className="min-w-0 animate-slide-up">
      <Routes location={location}>{children}</Routes>
    </div>
  )
}

function Shell({
  children,
  role,
  onToggleRole,
  onLogout,
}: {
  children: ReactNode
  role: AppRole
  onToggleRole: (role: AppRole) => void
  onLogout: () => void
}) {
  const lang = useLang()
  const navigate = useNavigate()

  function switchRole(nextRole: AppRole) {
    onToggleRole(nextRole)
    navigate(nextRole === 'provider' ? '/provider' : '/')
  }

  function logout() {
    onLogout()
    navigate('/')
  }

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-white/60 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex min-w-0 max-w-7xl flex-col gap-4 px-4 py-4 lg:px-6">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3" aria-label={t('shell.homeAria')}>
              <div className="grid h-11 w-11 place-items-center rounded-lg bg-primary-700 text-white shadow-lg shadow-emerald-900/15">
                <HeartPulse className="h-6 w-6" />
              </div>
              <div>
                <div className="text-lg font-semibold">SyriaPHR</div>
                <div className="text-xs text-stone-500">{t('app.tagline')}</div>
              </div>
            </Link>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleLang}
                aria-pressed={lang === 'ar'}
                aria-label={t('shell.toggleLangAria')}
                title={lang === 'ar' ? t('shell.switchToEnglish') : t('shell.switchToArabic')}
                className={`flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition active:scale-95 ${
                  lang === 'ar'
                    ? 'border-primary-200 bg-primary-50 text-primary-800'
                    : 'border-stone-200 bg-white text-stone-600 hover:border-emerald-200 hover:text-emerald-900'
                }`}
              >
                <Languages className="h-4 w-4" />
                <span className="hidden sm:inline">{lang === 'ar' ? t('shell.englishLabel') : t('shell.arabicLabel')}</span>
              </button>
              <div className="hidden items-center gap-3 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 sm:flex">
                <ShieldCheck className="h-4 w-4" />
                <span>{role === 'provider' ? t('shell.rosterActive') : t('shell.verifiedNhi')}</span>
              </div>
              <button
                type="button"
                onClick={logout}
                className="flex h-10 items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 text-sm font-medium text-stone-600 transition hover:border-rose-200 hover:text-rose-700 active:scale-95"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
          <nav className="flex w-full max-w-full gap-2 overflow-x-auto pb-1" aria-label={t('shell.primaryNav')}>
            {role === 'patient' ? (
              <>
                <NavItem to="/" icon={<Home className="h-4 w-4" />} label={tn('myChart')} />
                {domains.map((domain) => (
                  <NavItem key={domain.key} to={domain.route} icon={domain.icon} label={dTitle(domain.labelKey)} />
                ))}
                <NavItem to="/nhi/register" icon={<IdCard className="h-4 w-4" />} label={tn('myNHI')} />
              </>
            ) : (
              <>
                <NavItem to="/provider" icon={<User className="h-4 w-4" />} label={tn('roster')} />
                <NavItem to="/register" icon={<Plus className="h-4 w-4" />} label={tn('register')} />
                <NavItem to="/search" icon={<Search className="h-4 w-4" />} label={tn('search')} />
                <button
                  type="button"
                  onClick={() => switchRole('patient')}
                  className="flex h-10 shrink-0 items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 text-sm font-medium text-stone-600 transition hover:border-emerald-200 hover:text-emerald-900 active:scale-95"
                >
                  <Home className="h-4 w-4" />
                  {tn('patientMode')}
                </button>
              </>
            )}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6 lg:px-6">{children}</main>
    </>
  )
}

function NavItem({ to, icon, label }: { to: string; icon: ReactNode; label: string }) {
  const { pathname } = useLocation()
  const active = pathname === to
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={`flex h-10 shrink-0 items-center gap-2 rounded-lg px-3 text-sm font-medium transition active:scale-95 ${
        active
          ? 'bg-primary-700 text-white shadow-md shadow-emerald-900/15'
          : 'border border-stone-200 bg-white text-stone-600 hover:border-emerald-200 hover:text-emerald-900'
      }`}
    >
      {icon}
      <span>{label}</span>
    </Link>
  )
}

/* ============================================================
   Dashboard
   ============================================================ */

function Dashboard() {
  const [bannerToast, setBannerToast] = useState('')

  function showToast(message: string) {
    setBannerToast(message)
    window.setTimeout(() => setBannerToast(''), 2600)
  }

  const bannerPerson = {
    name_ar: 'أحمد محمد الحسن',
    name_latin: 'Ahmad Al-Hassan',
    nhi: 'SYR-2026-000001',
    mrn: 'DAM-014482',
    facility: 'Al-Mouwasat University Hospital',
    dob: '1979-03-15',
    sex: 'M',
    phone: '+963 944 555 123',
    address: { city: 'Mazzeh', governorate: 'Damascus' },
    verificationTier: 4 as const,
  }

  const bannerAllergies = [
    { id: 'penicillin', name: 'Penicillin', severity: 'moderate' as const, type: 'drug' as const },
    { id: 'peanut', name: 'Peanut', severity: 'severe' as const, type: 'food' as const },
  ]

  const bannerVitals = [
    { id: 'height', label: t('vital.height'), value: '175', unit: 'cm', date: '2026-08-12', trend: '—' },
    { id: 'weight', label: t('vital.weight'), value: '82', unit: 'kg', date: '2026-08-12', trend: fmt(t('dash.trendThisMonth'), { delta: '−1.2 kg' }) },
    { id: 'bmi', label: t('vital.bmi'), value: '26.8', unit: 'kg/m²', date: '2026-08-12', trend: '—' },
    { id: 'bp', label: t('vital.bp'), value: '128/84', unit: 'mmHg', date: '2026-08-12', trend: fmt(t('dash.trendVsLast'), { delta: '−6' }) },
    { id: 'hba1c', label: t('vital.hba1c'), value: '7.4', unit: '%', date: '2026-08-12', trend: fmt(t('dash.trendVsLast'), { delta: '+0.2' }) },
  ]

  const bannerDirectives = [
    {
      id: 'living-will',
      type: t('banner.directiveType.living-will'),
      date: '2026-06-01',
      status: 'active' as const,
      summary: t('dash.directive.livingWillSummary'),
    },
    {
      id: 'dnr-discussion',
      type: t('banner.directiveType.dnr-discussion'),
      date: '2026-08-20',
      status: 'draft' as const,
      summary: t('dash.directive.dnrSummary'),
    },
  ]

  const bannerLatestNote = {
    id: 'note-1',
    date: '2026-08-27',
    provider: 'Dr. Layla Haddad',
    text: t('dash.demoNote'),
  }

  return (
    <div className="space-y-6">
      {/* Patient banner — identity, vitals, allergies, directives, latest note */}
      <PatientBanner
        person={bannerPerson}
        allergies={bannerAllergies}
        vitals={bannerVitals}
        advanceDirectives={bannerDirectives}
        latestNote={bannerLatestNote}
        onAddNote={() => showToast(t('dash.toastNoteSoon'))}
        onViewAllNotes={() => showToast(t('dash.toastNotesSoon'))}
      />

      <ExplainMyChartLauncher patientId={EXPLAIN_DEMO_PATIENT_ID} patientName={bannerPerson.name_latin} />

      {bannerToast && (
        <div
          className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-primary-700 px-4 py-3 text-sm font-semibold text-white shadow-lift"
          role="status"
        >
          <span className="inline-flex items-center gap-2">
            <Check className="h-4 w-4" />
            {bannerToast}
          </span>
        </div>
      )}

      {/* Vitals sparkline cards */}
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label={tl('vitalsAria')}>
        <VitalCard
          label={tl('systolicBp')}
          value="128"
          unit="mmHg"
          trend="down"
          note={fmt(t('dash.trendVsLast'), { delta: '−6' })}
          points={[132, 130, 131, 129, 130, 128, 127, 129, 128]}
          color="#2f7357"
        />
        <VitalCard
          label={tl('diastolicBp')}
          value="82"
          unit="mmHg"
          trend="down"
          note={fmt(t('dash.trendVsLast'), { delta: '−3' })}
          points={[86, 85, 84, 85, 83, 84, 82, 83, 82]}
          color="#2f7357"
        />
        <VitalCard
          label={tl('weight')}
          value="78.4"
          unit="kg"
          trend="down"
          note={fmt(t('dash.trendThisMonth'), { delta: '−1.2 kg' })}
          points={[80.2, 79.8, 79.5, 79.1, 78.9, 78.6, 78.4]}
          color="#d95a2b"
        />
        <VitalCard
          label={tl('hba1c')}
          value="7.4"
          unit="%"
          trend="up"
          note={fmt(t('dash.trendVsLast'), { delta: '+0.2' })}
          points={[6.9, 7.0, 7.1, 7.0, 7.2, 7.3, 7.4]}
          color="#c04a20"
        />
      </section>

      {/* Slim banner (identity basics) → Explain My Chart → vitals →
          immunizations → devices → clinical domain cards (collapsible) →
          Documents & proof (collapsed) → encounters/documents (collapsed) →
          timeline. Heavyweight detail is deferred to secondary surfaces. */}
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label={tl('vitalsAria')}>
        <VitalCard
          label={tl('systolicBp')}
          value="128"
          unit="mmHg"
          trend="down"
          note={fmt(t('dash.trendVsLast'), { delta: '−6' })}
          points={[132, 130, 131, 129, 130, 128, 127, 129, 128]}
          color="#2f7357"
        />
        <VitalCard
          label={tl('diastolicBp')}
          value="82"
          unit="mmHg"
          trend="down"
          note={fmt(t('dash.trendVsLast'), { delta: '−3' })}
          points={[86, 85, 84, 85, 83, 84, 82, 83, 82]}
          color="#2f7357"
        />
        <VitalCard
          label={tl('weight')}
          value="78.4"
          unit="kg"
          trend="down"
          note={fmt(t('dash.trendThisMonth'), { delta: '−1.2 kg' })}
          points={[80.2, 79.8, 79.5, 79.1, 78.9, 78.6, 78.4]}
          color="#d95a2b"
        />
        <VitalCard
          label={tl('hba1c')}
          value="7.4"
          unit="%"
          trend="up"
          note={fmt(t('dash.trendVsLast'), { delta: '+0.2' })}
          points={[6.9, 7.0, 7.1, 7.0, 7.2, 7.3, 7.4]}
          color="#c04a20"
        />
      </section>

      {/* Immunizations — recommendations + given vaccines */}
      <ImmunizationsPanel patientId={CHART_PATIENT_ID} />

      {/* Devices — patient devices */}
      <DevicesPanel patientId={CHART_PATIENT_ID} />

      {/* Collapsible clinical domain cards — one-line summaries by default */}
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5" aria-label={tl('clinicalSectionsAria')}>
        {domains.map((domain) => (
          <DomainCollapsibleCard key={domain.key} domain={domain} />
        ))}
      </section>

      {/* Documents & proof — SMART Health Card/Link QRs collapsed by default */}
      <CollapsibleSection
        title={t('dash.documentsProof')}
        summary={t('dash.documentsProofSummary')}
        icon={<ShieldCheck className="h-5 w-5 text-primary-700" />}
        testId="documents-proof-section"
        defaultOpen={false}
      >
        <PortableProof />
      </CollapsibleSection>

      {/* Encounters + documents — collapsed by default */}
      <CollapsibleSection
        title={t('dash.encDocs')}
        summary={t('dash.encDocsSummary')}
        icon={<Stethoscope className="h-5 w-5 text-primary-700" />}
        testId="encounters-documents"
        defaultOpen={false}
      >
        <div className="grid gap-4 xl:grid-cols-2">
          <VitalsPanel patientId={CHART_PATIENT_ID} />
          <RecentEncounters patientId={CHART_PATIENT_ID} />
        </div>
        <div className="grid gap-4 xl:grid-cols-2">
          <Documents patientId={CHART_PATIENT_ID} />
        </div>
      </CollapsibleSection>

      {/* Health timeline strip — kept, demoted below the clinical content */}
      <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft" aria-label={tl('timelineAria')}>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary-700" />
            <h2 className="text-lg font-semibold text-stone-950">{tl('recentActivity')}</h2>
          </div>
          <span className="text-xs text-stone-500">{tl('scrollMore')}</span>
        </div>
        <div className="timeline-scroll no-scrollbar -mx-1 flex gap-3 overflow-x-auto px-1 pb-2">
          <TimelineEvent
            icon={<FlaskConical className="h-4 w-4" />}
            tone="bg-emerald-50 text-emerald-700"
            title={t('tl.hba1c.title')}
            detail={t('tl.hba1c.detail')}
            time={t('tl.hba1c.time')}
          />
          <TimelineEvent
            icon={<Pill className="h-4 w-4" />}
            tone="bg-indigo-50 text-indigo-700"
            title={t('tl.metformin.title')}
            detail={t('tl.metformin.detail')}
            time={t('tl.metformin.time')}
          />
          <TimelineEvent
            icon={<FileText className="h-4 w-4" />}
            tone="bg-sky-50 text-sky-700"
            title={t('tl.diagnosis.title')}
            detail={t('tl.diagnosis.detail')}
            time={t('tl.diagnosis.time')}
          />
          <TimelineEvent
            icon={<Activity className="h-4 w-4" />}
            tone="bg-amber-50 text-amber-700"
            title={t('tl.procedure.title')}
            detail={t('tl.procedure.detail')}
            time={t('tl.procedure.time')}
          />
          <TimelineEvent
            icon={<AlertTriangle className="h-4 w-4" />}
            tone="bg-rose-50 text-rose-700"
            title={t('tl.allergy.title')}
            detail={t('tl.allergy.detail')}
            time={t('tl.allergy.time')}
          />
          <TimelineEvent
            icon={<Stethoscope className="h-4 w-4" />}
            tone="bg-stone-100 text-stone-700"
            title={t('tl.checkup.title')}
            detail={t('tl.checkup.detail')}
            time={t('tl.checkup.time')}
          />
        </div>
      </section>
    </div>
  )
}

/* ============================================================
   Collapsible building blocks (dashboard simplification)
   ============================================================ */

/**
 * Collapsible card base — header row with title + one-line summary and a
 * chevron; content renders only when open. Used by Documents & proof, the
 * encounters/documents group, and the clinical domain cards.
 */
function CollapsibleCard({
  title,
  summary,
  icon,
  open,
  onToggle,
  testId,
  children,
}: {
  title: string
  summary?: string
  icon?: ReactNode
  open: boolean
  onToggle: () => void
  testId?: string
  children: ReactNode
}) {
  return (
    <div
      className={`overflow-hidden rounded-xl border border-stone-200 bg-white shadow-soft transition ${open ? 'sm:col-span-2 xl:col-span-5' : ''}`}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : undefined}
        className="flex w-full items-center gap-4 p-5 text-start transition hover:bg-stone-50/70"
      >
        {icon && (
          <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-lg ring-1 ${open ? 'bg-primary-50 text-primary-700 ring-primary-200' : 'bg-stone-50 text-stone-500 ring-stone-100'}`}>
            {icon}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-lg font-semibold text-stone-950">{title}</span>
          {summary && !open && (
            <span className="mt-1 block truncate text-sm leading-6 text-stone-600">{summary}</span>
          )}
        </span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && <div className="p-5 pt-0">{children}</div>}
    </div>
  )
}

/** Whole-row collapsible section wrapper (grid container keeps spacing). */
function CollapsibleSection({
  title,
  summary,
  icon,
  testId,
  defaultOpen = false,
  children,
}: {
  title: string
  summary?: string
  icon?: ReactNode
  testId?: string
  defaultOpen?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <CollapsibleCard
      title={title}
      summary={summary}
      icon={icon}
      open={open}
      onToggle={() => setOpen((value) => !value)}
      testId={testId}
    >
      {children}
    </CollapsibleCard>
  )
}

/**
 * Clinical domain card — collapsed by default to a one-line summary
 * (entry count + record eyebrow); expanding reveals the existing deep-link
 * chips into the full domain page. Nothing was removed — the full records,
 * lookup tables, and add-entry forms all live on the domain route as before.
 */
function DomainCollapsibleCard({ domain }: { domain: DomainConfig }) {
  const [open, setOpen] = useState(false)
  const n = domain.initialRecords.length
  return (
    <CollapsibleCard
      title={dTitle(domain.labelKey)}
      summary={
        n > 0
          ? `${fmt(n === 1 ? th('nEntries') : th('nEntriesPlural'), { n })} · ${domain.eyebrow}`
          : domain.eyebrow
      }
      icon={<span className={`grid h-11 w-11 place-items-center rounded-lg ${domain.bg} ${domain.accent} ring-1 ${domain.ring}`}>{domain.icon}</span>}
      open={open}
      onToggle={() => setOpen((value) => !value)}
      testId={`domain-card-${domain.key}`}
    >
      <p className="text-sm leading-6 text-stone-600">{dSub(domain.labelKey)}</p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Link
          to={domain.route}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary-700 px-3 py-2 text-sm font-semibold text-white transition hover:bg-primary-800 active:scale-95"
        >
          {fmt(t('dash.openDomain'), { title: dTitle(domain.labelKey) })}
          <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
        </Link>
        {domain.initialRecords.map((record) => (
          <span
            key={record.id}
            className="inline-flex max-w-full items-center rounded-full border border-stone-200 bg-stone-50 px-2.5 py-1 text-xs font-medium text-stone-700"
          >
            <span className="truncate">{demoText(String(record.name))}</span>
          </span>
        ))}
      </div>
    </CollapsibleCard>
  )
}

function VitalCard({
  label,
  value,
  unit,
  trend,
  note,
  points,
  color,
}: {
  label: string
  value: string
  unit: string
  trend: 'up' | 'down'
  note: string
  points: number[]
  color: string
}) {
  const path = useMemo(() => sparklinePath(points), [points])
  const trendColor = trend === 'down' ? 'text-emerald-700' : 'text-amber-700'
  const trendArrow = trend === 'down' ? '↓' : '↑'

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft transition hover:shadow-md">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-stone-600">{label}</span>
        <span className={`text-xs font-semibold ${trendColor}`}>{trendArrow} {note}</span>
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className="text-3xl font-semibold text-stone-950">{value}</span>
        <span className="text-sm text-stone-500">{unit}</span>
      </div>
      <svg
        viewBox="0 0 100 36"
        className="mt-3 h-10 w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label={fmt(tl('trendAria'), { label })}
      >
        <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d={`${path} L 100 36 L 0 36 Z`} fill={color} opacity="0.08" />
      </svg>
    </div>
  )
}

function sparklinePath(points: number[]) {
  const min = Math.min(...points)
  const max = Math.max(...points)
  const range = max - min || 1
  const step = 100 / (points.length - 1)
  return points
    .map((point, index) => {
      const x = index * step
      const y = 34 - ((point - min) / range) * 30
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
}

function TimelineEvent({
  icon,
  tone,
  title,
  detail,
  time,
}: {
  icon: ReactNode
  tone: string
  title: string
  detail: string
  time: string
}) {
  return (
    <div className="flex w-56 shrink-0 flex-col gap-2 rounded-lg border border-stone-200 bg-stone-50/60 p-4 transition hover:border-emerald-200 hover:bg-white hover:shadow-md">
      <div className="flex items-center justify-between">
        <div className={`grid h-8 w-8 place-items-center rounded-lg ${tone}`}>{icon}</div>
        <span className="text-xs text-stone-400">{time}</span>
      </div>
      <div className="text-sm font-semibold text-stone-900">{title}</div>
      <div className="text-xs leading-5 text-stone-500">{detail}</div>
    </div>
  )
}

/* ============================================================
   Patient portal (Sprint 2 — registration + search + profile)
   ============================================================ */

function PatientPortal({ mode }: { mode: 'register' | 'search' }) {
  const [profileId, setProfileId] = useState<string | null>(null)
  const [registered, setRegistered] = useState<RegistrationResponse | null>(null)

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="grid h-12 w-12 place-items-center rounded-lg bg-primary-50 text-primary-700 ring-1 ring-primary-200">
              <User className="h-6 w-6" />
            </div>
            <div>
              <div className="text-sm font-medium text-stone-500">{t('portal.eyebrow')}</div>
              <h2 className="text-3xl font-semibold text-stone-950">
                {mode === 'register' ? t('portal.registerTitle') : t('portal.searchTitle')}
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">
                {mode === 'register' ? t('portal.registerDesc') : t('portal.searchDesc')}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link
              to="/register"
              className={`flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-semibold transition active:scale-95 ${
                mode === 'register'
                  ? 'border-primary-200 bg-primary-50 text-primary-800'
                  : 'border-stone-200 bg-white text-stone-600 hover:border-emerald-200'
              }`}
            >
              <Plus className="h-4 w-4" />
              {t('portal.register')}
            </Link>
            <Link
              to="/search"
              className={`flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-semibold transition active:scale-95 ${
                mode === 'search'
                  ? 'border-primary-200 bg-primary-50 text-primary-800'
                  : 'border-stone-200 bg-white text-stone-600 hover:border-emerald-200'
              }`}
            >
              <Search className="h-4 w-4" />
              {t('portal.search')}
            </Link>
          </div>
        </div>
      </section>

      {mode === 'register' ? (
        <PatientRegistration
          onRegistered={(result) => {
            setRegistered(result)
            setProfileId(result.patient.id)
          }}
          onCancel={() => setRegistered(null)}
        />
      ) : (
        <PatientSearch onSelect={(candidate) => setProfileId(candidate.patient_id ?? candidate.person_id)} />
      )}

      {registered && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" role="status">
          <span className="inline-flex items-center gap-2 font-semibold">
            <Check className="h-4 w-4" />
            {fmt(t('portal.registeredOk'), { nhi: registered.patient.person.nhi, action: portalActionLabel(registered.action) })}
          </span>
        </div>
      )}

      {profileId && (
        <PatientProfile patientId={profileId} onClose={() => setProfileId(null)} />
      )}
    </div>
  )
}

/* ============================================================
   Domain page
   ============================================================ */

function DomainPage({ config }: { config: DomainConfig }) {
  const [records, setRecords] = useState(config.initialRecords)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [open, setOpen] = useState(false)
  const [formError, setFormError] = useState('')
  const [toast, setToast] = useState('')
  const [addedChip, setAddedChip] = useState('')
  const [loading, setLoading] = useState(true)
  const [provenance, setProvenance] = useState<'patient' | 'clinician'>('patient')
  const [form, setForm] = useState<Record<string, string>>(() => initialForm(config.formFields, config.provenance))
  const searchRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLDivElement>(null)

  const provenanceEnabled = Boolean(config.provenance)
  const prov = provenanceEnabled && config.provenance !== false ? config.provenance : null
  const activeFields = prov
    ? fieldsByKeys(provenance === 'clinician' ? prov.clinicianFields : prov.patientFields)
    : config.formFields

  /* Reset form when provenance mode toggles */
  useEffect(() => {
    if (!prov) return
    setForm(initialForm(config.formFields, config.provenance))
    setFormError('')
  }, [provenance]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Simulated 400ms table load shimmer */
  useEffect(() => {
    setLoading(true)
    const timer = window.setTimeout(() => setLoading(false), 400)
    return () => window.clearTimeout(timer)
  }, [config.key])

  /* Debounced search */
  useEffect(() => {
    setSearching(true)
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query)
      setSearching(false)
    }, 250)
    return () => window.clearTimeout(timer)
  }, [query])

  /* Cmd/Ctrl+K focuses search */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  /* Escape closes form */
  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])

  /* Focus trap while form open */
  useEffect(() => {
    if (!open) return
    const node = formRef.current
    if (!node) return
    const focusables = node.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    first.focus()
    function trap(event: KeyboardEvent) {
      if (event.key !== 'Tab') return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    node.addEventListener('keydown', trap)
    return () => node.removeEventListener('keydown', trap)
  }, [open])

  // Lookup rows: fetched from the terminology service when the domain sets
  // `lookupSystem`; otherwise filtered from the hardcoded `config.lookup`.
  const lookupRows = useTerminologyLookup(config.lookupSystem, config.lookup, debouncedQuery)
  const filteredLookup = useMemo(() => {
    if (config.lookupSystem) return lookupRows
    const q = debouncedQuery.trim().toLowerCase()
    if (!q) return config.lookup
    return config.lookup.filter((item) => Object.values(item).some((value) => value.toLowerCase().includes(q)))
  }, [config.lookupSystem, config.lookup, debouncedQuery, lookupRows])

  function submit(event: FormEvent) {
    event.preventDefault()
    const today = new Date().toISOString().slice(0, 10)

    // Per-provenance validation (mirrors the design doc §7 validation table).
    if (provenanceEnabled) {
      const err = validateProvenance(provenance, form)
      if (err) {
        setFormError(err)
        return
      }
    } else {
      if (!form.code?.trim() || !form.name?.trim()) {
        setFormError(th('codeNameRequired'))
        return
      }
    }

    if (activeFields.some((field) => field.type === 'date' && form[field.key] && form[field.key] > today)) {
      setFormError(th('noFutureDates'))
      return
    }
    setFormError('')
    const row: RecordItem = {
      id: Date.now(),
      ...form,
      reported_by: provenanceEnabled ? provenance : '',
    }
    setRecords((current) => [row, ...current])
    setForm(initialForm(config.formFields, config.provenance))
    setOpen(false)
    setToast(fmt(th('entrySaved'), { title: dTitle(config.labelKey) }))
    window.setTimeout(() => setToast(''), 2600)
  }

  function pickLookup(item: LookupItem) {
    setFormError('')
    setForm((current) => ({
      ...current,
      code: item.code ?? current.code,
      name: item.name ?? current.name,
      description: item.name ?? current.description,
      system: item.system ?? current.system,
    }))
    setOpen(true)
    setAddedChip(item.name ?? '')
    window.setTimeout(() => setAddedChip(''), 2000)
  }

  return (
    <div className="space-y-5">
      {/* Page header */}
      <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className={`grid h-12 w-12 place-items-center rounded-lg ${config.bg} ${config.accent} ring-1 ${config.ring}`}>
              {config.icon}
            </div>
            <div>
              <div className="text-sm font-medium text-stone-500">{config.eyebrow}</div>
              <h2 className="text-3xl font-semibold text-stone-950">{dTitle(config.labelKey)}</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">{dSub(config.labelKey)}</p>
            </div>
          </div>
          <Button onClick={() => setOpen(true)} icon={<Plus className="h-4 w-4" />}>
            {th('addEntry')}
          </Button>
        </div>
      </section>

      {/* Add form (focus-trapped) */}
      {open && (
        <section
          ref={formRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-form-title"
          className="animate-pop-in rounded-xl border border-emerald-100 bg-white p-5 shadow-lift"
        >
          <div className="mb-4 flex items-center justify-between">
            <h3 id="add-form-title" className="text-lg font-semibold text-stone-950">
              {fmt(th('newEntry'), { title: dTitle(config.labelKey) })}
            </h3>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={th('closeForm')}
              className="grid h-9 w-9 place-items-center rounded-lg border border-stone-200 text-stone-600 transition hover:bg-stone-50 active:scale-90"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
            {provenanceEnabled && config.provenance && (
              <div className="md:col-span-2">
                <ProvenanceToggle
                  label={config.provenance.toggleLabel}
                  patientLabel={config.provenance.patientLabel}
                  clinicianLabel={config.provenance.clinicianLabel}
                  value={provenance}
                  onChange={setProvenance}
                />
              </div>
            )}
            {activeFields.map((field) => (
              <label
                key={field.key}
                className={`grid gap-2 text-sm font-medium text-stone-700 ${field.fullWidth ? 'md:col-span-2' : ''}`}
              >
                {field.label}
                {field.type === 'select' ? (
                  field.lookupSystem ? (
                    <TerminologySelect
                      system={field.lookupSystem}
                      value={form[field.key] ?? ''}
                      onChange={(value) => setForm({ ...form, [field.key]: value })}
                    />
                  ) : (
                    <select
                      value={form[field.key] ?? ''}
                      onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}
                      className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                    >
                      {field.options?.map((option) => (
                        <option key={option}>{option}</option>
                      ))}
                    </select>
                  )
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={form[field.key] ?? ''}
                    placeholder={field.placeholder}
                    onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}
                    rows={3}
                    className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                  />
                ) : (
                  <input
                    type={field.type ?? 'text'}
                    value={form[field.key] ?? ''}
                    placeholder={field.placeholder}
                    onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}
                    className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                  />
                )}
              </label>
            ))}
            {formError && (
              <p className="md:col-span-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-800" role="alert">
                {formError}
              </p>
            )}
            <div className="flex gap-3 md:col-span-2">
              <Button type="submit" icon={<Check className="h-4 w-4" />}>
                {th('saveEntry')}
              </Button>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                {th('cancel')}
              </Button>
            </div>
          </form>
        </section>
      )}

      <section className="grid min-w-0 gap-5 xl:grid-cols-[1.1fr_0.9fr]">
        <DataPanel
          title={th('patientRecord')}
          rows={records}
          columns={config.recordColumns}
          loading={loading}
          onDelete={(id) => setRecords((current) => current.filter((row) => row.id !== id))}
        />
        <LookupPanel
          config={config}
          query={query}
          setQuery={setQuery}
          searching={searching}
          rows={filteredLookup}
          onPick={pickLookup}
          searchRef={searchRef}
        />
      </section>

      {/* "Added to form" chip */}
      {addedChip && (
        <div className="fixed bottom-6 right-6 z-50 flex animate-chip-in items-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-emerald-800 shadow-lift">
          <Check className="h-4 w-4 text-emerald-600" />
          <span className="max-w-56 truncate">{addedChip}</span>
          <span className="text-stone-400">{th('addedToForm')}</span>
        </div>
      )}

      {toast && (
        <div
          className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-primary-700 px-4 py-3 text-sm font-semibold text-white shadow-lift"
          role="status"
        >
          <span className="inline-flex items-center gap-2">
            <Check className="h-4 w-4" />
            {toast}
          </span>
        </div>
      )}
    </div>
  )
}

/* ============================================================
   Data panel (patient record table)
   ============================================================ */

function DataPanel({
  title,
  rows,
  columns,
  loading,
  onDelete,
}: {
  title: string
  rows: RecordItem[]
  columns: { key: string; label: string }[]
  loading: boolean
  onDelete: (id: RecordItem['id']) => void
}) {
  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-soft">
      <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4">
        <h3 className="font-semibold text-stone-950">{title}</h3>
        <span className="rounded-md bg-stone-100 px-2 py-0.5 text-xs font-semibold text-stone-600">
          {fmt(rows.length === 1 ? th('nEntries') : th('nEntriesPlural'), { n: rows.length })}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-stone-50 text-stone-500">
            <tr>
              {columns.map((column) => (
                <th key={column.key} className="px-5 py-3 font-medium">{column.label}</th>
              ))}
              <th className="px-5 py-3"><span className="sr-only">{th('actions')}</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading ? (
              <SkeletonRows count={3} cells={columns.length + 1} />
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="px-5 py-10 text-center text-sm text-stone-500">
                  {th('noEntries')}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="transition hover:bg-emerald-50/50">
                  {columns.map((column) => (
                    <td key={column.key} className="whitespace-nowrap px-5 py-4 text-stone-700">
                      {column.key === 'code' ? (
                        <code className="ltr-keep font-mono text-emerald-800">{row[column.key]}</code>
                      ) : isStatusColumn(column.key) ? (
                        <StatusPill value={String(row[column.key])} />
                      ) : column.key === 'reported_by' ? (
                        <ProvenanceBadge value={String(row[column.key])} />
                      ) : column.key === 'reaction' ? (
                        vocab(String(row[column.key]))
                      ) : column.key === 'note' ? (
                        <NoteCell value={row[column.key]} />
                      ) : column.key === 'name' && 'reported_by' in row ? (
                        <MedNameCell row={row} />
                      ) : (
                        renderDemoCell(row[column.key])
                      )}
                    </td>
                  ))}
                  <td className="whitespace-nowrap px-5 py-4 text-right">
                    <button
                      type="button"
                      onClick={() => onDelete(row.id)}
                      aria-label={fmt(th('deleteAria'), { name: row.name ? demoText(String(row.name)) : th('entryWord') })}
                      className="grid h-8 w-8 place-items-center rounded-lg border border-stone-200 text-stone-400 transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 active:scale-90"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function SkeletonRows({ count, cells }: { count: number; cells: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, rowIndex) => (
        <tr key={rowIndex}>
          {Array.from({ length: cells }).map((_, cellIndex) => (
            <td key={cellIndex} className="px-5 py-4">
              <div className="skeleton h-4 w-24" />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

/* Status pill coloring by value (canonical English value even when label is translated) */
function StatusPill({ value }: { value: string }) {
  const normalized = (statusFromLabel(value) ?? value).toLowerCase()
  let tone = 'bg-stone-100 text-stone-700 border-stone-200'
  if (['active', 'completed'].includes(normalized)) tone = 'bg-emerald-50 text-emerald-800 border-emerald-200'
  else if (['resolved', 'inactive', 'cancelled', 'historic', 'paused'].includes(normalized))
    tone = 'bg-stone-100 text-stone-600 border-stone-200'
  else if (['life-threatening', 'severe', 'entered in error'].includes(normalized))
    tone = 'bg-rose-50 text-rose-800 border-rose-200'
  else if (['moderate', 'planned'].includes(normalized)) tone = 'bg-amber-50 text-amber-800 border-amber-200'
  else if (['mild'].includes(normalized)) tone = 'bg-sky-50 text-sky-800 border-sky-200'

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold ${tone}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {statusLabel(value)}
    </span>
  )
}

function isStatusColumn(key: string) {
  return ['status', 'severity'].includes(key)
}

/* ============================================================
   Dual-provenance rendering (meds domain)
   ============================================================ */

/** Provenance badge — patient = blue "Patient reported", clinician = emerald "Prescribed". */
function ProvenanceBadge({ value }: { value: string }) {
  const isPatient = value === 'patient'
  const tone = isPatient
    ? 'bg-sky-50 text-sky-800 border-sky-200'
    : 'bg-emerald-50 text-emerald-800 border-emerald-200'
  const label = isPatient ? tf('patientReported') : tf('prescribed')
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold ${tone}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {label}
    </span>
  )
}

/**
 * Medication name cell — renders the med name, then a compact SIG block for
 * clinician rows. The narrative note (patient rows) lives in its own `note`
 * column (see NoteCell), so it is not duplicated here.
 */
function MedNameCell({ row }: { row: RecordItem }) {
  const name = String(row.name ?? row.description ?? '')
  const isClinician = row.reported_by === 'clinician'
  return (
    <div className="min-w-0">
      <div className="font-medium text-stone-900">{name}</div>
      {isClinician ? (
        <div className="mt-1 text-xs leading-5 text-stone-500">
          {formatSig(row)}
          {row.prescriber ? (
            <span className="mt-0.5 block text-stone-400">— {row.prescriber}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Note column — renders the narrative/SIG note when present, else a muted dash.
 * Patient rows show the narrative in italics; clinician rows show the SIG note.
 */
function NoteCell({ value }: { value: string | number | undefined }) {
  const text = value ? String(value).trim() : ''
  if (!text) {
    return <span className="text-stone-300">—</span>
  }
  return <span className="block max-w-64 truncate text-xs italic leading-5 text-stone-500">{demoText(text)}</span>
}

function renderDemoCell(value: string | number | undefined) {
  if (value === undefined || value === null || value === '') return '—'
  if (typeof value === 'number') return value
  return demoText(value)
}

/** Build the compact SIG string, e.g. "500 mg · Oral tablet · BID · 30 days · Qty 60 · 0 refills". */
function formatSig(row: RecordItem) {
  const parts: string[] = []
  const dose = row.dose ? String(row.dose) : ''
  const unit = row.dose_unit ? String(row.dose_unit) : ''
  if (dose) parts.push(unit ? `${dose} ${unit}` : dose)
  const route = row.route ? String(row.route) : ''
  const form = row.form ? String(row.form) : ''
  const routeLabel = demoText(route)
  const formLabel = demoText(form)
  if (route && form) parts.push(`${routeLabel} ${formLabel}`)
  else if (route) parts.push(routeLabel)
  else if (form) parts.push(formLabel)
  if (row.frequency_code) parts.push(demoText(String(row.frequency_code)))
  if (row.duration) parts.push(demoText(String(row.duration)))
  if (row.quantity) parts.push(`${t('sig.qty')} ${demoText(String(row.quantity))}`)
  if (row.refills !== undefined && row.refills !== null && row.refills !== '') {
    parts.push(`${row.refills} ${Number(row.refills) === 1 ? t('sig.refill') : t('sig.refills')}`)
  }
  return parts.join(' · ')
}

/* ============================================================
   Lookup panel
   ============================================================ */

function LookupPanel({
  config,
  query,
  setQuery,
  searching,
  rows,
  onPick,
  searchRef,
}: {
  config: DomainConfig
  query: string
  setQuery: (value: string) => void
  searching: boolean
  rows: LookupItem[]
  onPick: (item: LookupItem) => void
  searchRef: React.RefObject<HTMLInputElement>
}) {
  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-soft">
      <div className="border-b border-stone-100 p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="font-semibold text-stone-950">{th('lookupTable')}</h3>
          <span className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${config.bg} ${config.accent}`}>
            {config.eyebrow}
          </span>
        </div>
        <div className="relative">
          <label className="flex h-11 items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 text-sm transition focus-within:border-emerald-500 focus-within:bg-white focus-within:ring-4 focus-within:ring-emerald-100">
            <Search className="h-4 w-4 shrink-0 text-stone-400" />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={th('searchPlaceholder')}
              aria-label={th('searchLookupAria')}
              className="w-full bg-transparent text-stone-950 outline-none placeholder:text-stone-400"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label={th('clearSearch')}
                className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-stone-400 transition hover:bg-stone-200 hover:text-stone-700 active:scale-90"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </label>
          <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-stone-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-stone-400 sm:block rtl:left-3 rtl:right-auto">
            ⌘K
          </kbd>
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-stone-500">
          {searching ? (
            <span className="inline-flex items-center gap-1.5 text-emerald-700">
              <span className="h-3 w-3 animate-pulse-soft rounded-full bg-emerald-500" aria-hidden="true" />
              {th('searching')}
            </span>
          ) : (
            <span>{query ? fmt(th('resultsFor'), { q: query }) : th('allTerms')}</span>
          )}
          <span className="rounded-md bg-stone-100 px-2 py-0.5 font-semibold text-stone-600">
            {fmt(rows.length === 1 ? th('nResults') : th('nResultsPlural'), { n: rows.length })}
          </span>
        </div>
      </div>
      <div className="max-h-[520px] overflow-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 bg-stone-50 text-stone-500">
            <tr>
              {config.lookupColumns.map((column) => (
                <th key={column.key} className="px-4 py-3 font-medium">{column.label}</th>
              ))}
              <th className="px-4 py-3"><span className="sr-only">{th('useTermAria')}</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={config.lookupColumns.length + 1} className="px-4 py-10 text-center text-sm text-stone-500">
                  {th('noMatchingTerms')}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={`${row.code}-${row.name}`}
                  tabIndex={0}
                  role="button"
                  aria-label={fmt(th('useToPrefillAria'), { name: demoText(row.name) })}
                  title={th('clickToPrefill')}
                  className="group cursor-pointer transition hover:bg-emerald-50/60 focus:bg-emerald-50 focus:outline-none"
                  onClick={() => onPick(row)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onPick(row)
                    }
                  }}
                >
                  {config.lookupColumns.map((column) => (
                    <td key={column.key} className="whitespace-nowrap px-4 py-3 text-stone-700">
                      {column.key === 'code' ? (
                        <code className="ltr-keep font-mono text-emerald-800">{row[column.key]}</code>
                      ) : (
                        renderDemoCell(row[column.key])
                      )}
                    </td>
                  ))}
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 opacity-0 transition group-hover:opacity-100 group-focus:opacity-100">
                      <Plus className="h-3.5 w-3.5" />
                      {th('use')}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ============================================================
   Shared UI primitives
   ============================================================ */

function Button({
  children,
  onClick,
  type = 'button',
  icon,
  variant = 'primary',
}: {
  children: ReactNode
  onClick?: () => void
  type?: 'button' | 'submit'
  icon?: ReactNode
  variant?: 'primary' | 'secondary'
}) {
  const base =
    'flex h-11 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold transition active:scale-95'
  const styles =
    variant === 'primary'
      ? 'bg-primary-700 text-white shadow-md shadow-emerald-900/15 hover:bg-primary-800'
      : 'border border-stone-200 bg-white text-stone-700 hover:bg-stone-50'
  return (
    <button type={type} onClick={onClick} className={`${base} ${styles}`}>
      {icon}
      {children}
    </button>
  )
}

function initialForm(fields: Field[], provenance?: DomainConfig['provenance']) {
  const allFields = provenance
    ? fieldsByKeys([...provenance.patientFields, ...provenance.clinicianFields])
    : fields
  return allFields.reduce<Record<string, string>>((acc, field) => {
    acc[field.key] = field.options?.[0] ?? ''
    return acc
  }, {})
}

/* ============================================================
   Dual-provenance helpers (meds domain)
   ============================================================ */

/** Resolve a terminology lookup array by its system= tag (ADR-011). */
function sigOptions(system: string) {
  switch (system) {
    case 'fhir-route':
      return ROUTES
    case 'fhir-doseform':
      return FORMS
    case 'fhir-timing':
      return FREQUENCIES
    case 'ucum':
      return DOSE_UNITS
    default:
      return []
  }
}

/**
 * Select populated from the terminology service for a `lookupSystem`.
 * Options are fetched via `useTerminologyOptions` (API with local fallback).
 */
function TerminologySelect({
  system,
  value,
  onChange,
}: {
  system: string
  value: string
  onChange: (value: string) => void
}) {
  const options = useTerminologyOptions(system)
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
    >
      <option value="">{th('selectPlaceholder')}</option>
      {options.map((option) => (
        <option key={option.code} value={option.code}>
          {demoText(option.name)}
        </option>
      ))}
    </select>
  )
}

/**
 * Per-provenance validation — mirrors the design doc §7 validation table.
 * Patient mode: description required, narrative-only allowed, no discrete SIG.
 * Clinician mode: description required AND at least one discrete SIG field
 * (dose, route, form, or frequency) — a narrative note alone is rejected.
 */
function validateProvenance(mode: 'patient' | 'clinician', form: Record<string, string>): string {
  const description = form.description?.trim() || form.name?.trim()
  if (!description) {
    return th('medDescRequired')
  }
  if (mode === 'patient') {
    return ''
  }
  // Clinician mode: require at least one discrete SIG field.
  const sigKeys = ['dose', 'route', 'form', 'frequency_code']
  const hasSig = sigKeys.some((key) => form[key]?.trim())
  if (!hasSig) {
    return th('clinicianSigRequired')
  }
  return ''
}

/**
 * Segmented provenance control — role="radiogroup" with roving tabindex and
 * arrow-key navigation (Left/Right/Up/Down), per the WAI-ARIA radio group pattern.
 */
function ProvenanceToggle({
  label,
  patientLabel,
  clinicianLabel,
  value,
  onChange,
}: {
  label: string
  patientLabel: string
  clinicianLabel: string
  value: 'patient' | 'clinician'
  onChange: (key: 'patient' | 'clinician') => void
}) {
  const modes: { key: 'patient' | 'clinician'; label: string }[] = [
    { key: 'patient', label: patientLabel },
    { key: 'clinician', label: clinicianLabel },
  ]
  const activeIndex = Math.max(0, modes.findIndex((mode) => mode.key === value))

  function onKeyDown(event: React.KeyboardEvent, index: number) {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return
    event.preventDefault()
    let next = index
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % modes.length
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + modes.length) % modes.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = modes.length - 1
    onChange(modes[next].key)
  }

  return (
    <div>
      <span className="mb-2 block text-sm font-medium text-stone-700">{label}</span>
      <div
        role="radiogroup"
        aria-label={label}
        className="inline-flex rounded-lg border border-stone-200 bg-stone-50 p-1"
      >
        {modes.map((mode, index) => {
          const selected = mode.key === value
          return (
            <button
              key={mode.key}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(mode.key)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={`flex h-10 items-center gap-2 rounded-md px-4 text-sm font-semibold transition active:scale-95 ${
                selected
                  ? 'bg-white text-stone-950 shadow-sm ring-1 ring-stone-200'
                  : 'text-stone-500 hover:text-stone-800'
              }`}
            >
              <span
                aria-hidden="true"
                className={`h-2 w-2 rounded-full ${selected ? 'bg-emerald-600' : 'bg-stone-300'}`}
              />
              {mode.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default App
